// frontend/src/components/EdaPrepare/EdaPrepareTransformations.tsx

import React from 'react';

interface Props {
  analysis: any;
}

interface Decision {
  value?: string;
  column?: string;
  reason?: string;
  decision?: string;
  trace_id?: string;
  timestamp?: string;
  metrics?: Record<string, any>;
}

const METRIC_KEYS = [
  'count',
  'unique',
  'mean',
  'std',
  'min',
  'max',
  'top',
  'freq',
];

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(4);
  }
  return String(value);
};

const groupByDecision = (decisions: Decision[]): Record<string, Decision[]> => {
  const groups: Record<string, Decision[]> = {};
  for (const d of decisions) {
    const key = d.decision ?? 'unknown';
    if (!groups[key]) groups[key] = [];
    groups[key].push(d);
  }
  return groups;
};

const hasAnyMetric = (decisions: Decision[]): boolean => {
  return decisions.some(
    (d) => d.metrics && Object.keys(d.metrics).length > 0
  );
};

const DecisionTable: React.FC<{ decisions: Decision[] }> = ({ decisions }) => {
  const showMetrics = hasAnyMetric(decisions);

  return (
    <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
        color: '#ffffff'
      }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #1a4a7a' }}>
            <th style={thStyle}>Column</th>
            <th style={thStyle}>Value</th>
            <th style={thStyle}>Reason</th>
            <th style={thStyle}>Timestamp</th>
            {showMetrics && METRIC_KEYS.map((k) => (
              <th key={k} style={thStyle}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {decisions.map((d, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #16213e' }}>
              <td style={tdStyle}>{d.column ?? '—'}</td>
              <td style={tdStyle}>{d.value ?? '—'}</td>
              <td style={tdStyle}>{d.reason ?? '—'}</td>
              <td style={tdStyle}>{d.timestamp ?? '—'}</td>
              {showMetrics && METRIC_KEYS.map((k) => (
                <td key={k} style={tdStyle}>
                  {d.metrics && k in d.metrics ? formatValue(d.metrics[k]) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px',
  color: '#8899aa',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '8px',
  whiteSpace: 'nowrap',
};

export const EdaPrepareTransformations: React.FC<Props> = ({ analysis }) => {
  const transformations = analysis?.transformations || {};
  const decisions: Decision[] = Array.isArray(transformations.decisions)
    ? transformations.decisions
    : [];

  const groups = groupByDecision(decisions);
  const groupKeys = Object.keys(groups);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #1a4a7a',
      marginBottom: '24px'
    }}>
      <h3 style={{ color: '#4fc3f7', margin: '0 0 16px 0' }}>
        🔄 Transformações Aplicadas
      </h3>

      {decisions.length === 0 ? (
        <div style={{ fontSize: '14px', color: '#8899aa' }}>
          Nenhuma transformação registrada.
        </div>
      ) : (
        groupKeys.map((key) => (
          <div key={key} style={{ marginBottom: '20px' }}>
            <h4 style={{
              color: '#ffffff',
              margin: '0 0 8px 0',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              {key} <span style={{ color: '#8899aa', fontWeight: 400 }}>
                ({groups[key].length})
              </span>
            </h4>
            <DecisionTable decisions={groups[key]} />
          </div>
        ))
      )}
    </div>
  );
};